import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/auth";
import { Navigate } from "react-router-dom";
import SocialAuthButtons from "./SocialAuthButtons";
import { useTranslation } from "react-i18next";

interface SignupFormProps {
  isSubmitting: boolean;
  setIsSubmitting: (value: boolean) => void;
  setAuthMode: (mode: "login" | "signup" | "forgotPassword" | "resetPassword") => void;
  redirectPath?: string;
}

const SignupForm = ({ isSubmitting, setIsSubmitting, setAuthMode, redirectPath = "/" }: SignupFormProps) => {
  const { t } = useTranslation('auth');
  const { signUp, user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const formSchema = z.object({
    firstName: z.string().min(1, t("validation.firstNameRequired")),
    lastName: z.string().min(1, t("validation.lastNameRequired")),
    email: z.string().min(1, t("validation.emailRequired")).email(t("validation.emailInvalid")),
    password: z.string().min(6, t("validation.passwordMinLength")),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
    },
  });

  // If user is already authenticated, redirect
  if (user) {
    return <Navigate to={redirectPath} replace />;
  }

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);
    
    try {
      const result = await signUp(
        values.email, 
        values.password, 
        values.firstName, 
        values.lastName
      );

      if (result?.requiresEmailConfirmation) {
        setSuccess("Account created successfully. Please check your email to verify your account.");
        setAuthMode("login");
      } else {
        setSuccess("Account created successfully. You are now signed in.");
      }
    } catch (error: unknown) {
      console.error("Signup error:", error);
      const message = error instanceof Error ? error.message : 'An error occurred during signup';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="rounded-md bg-red-50 p-3 text-center text-sm text-coral">
              {error}
            </div>
          )}
          
          {success && (
            <div className="rounded-md bg-teal-soft p-3 text-center text-sm text-teal">
              {success}
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("signup.firstName")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("signup.firstNamePlaceholder")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("signup.lastName")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("signup.lastNamePlaceholder")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("signup.email")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("signup.emailPlaceholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("signup.password")}</FormLabel>
                <FormControl>
                  <Input type="password" placeholder={t("signup.passwordPlaceholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? t("signup.creatingAccount") : t("signup.createAccountButton")}
          </Button>
        </form>
      </Form>
      
      <SocialAuthButtons isSubmitting={isSubmitting} />
    </div>
  );
};

export default SignupForm;
